import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { take, finalize, Subscription, catchError, debounceTime, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';
import { LoadingService } from '../../loading/loading.service';
import { ModalComponent } from '../../modal/modal.component';
import { ToastService } from 'src/app/modules/services/toast/toast.service';
import { NotificacaoContratoService } from 'src/app/modules/services/notificacao-contrato/notificacao-contrato.service';
import { AlertComponent } from '../../alert/alert.component';
import { ModalHistoricComponent } from 'src/app/modules/shared/modals/modal-historic/modal-historic.component';
import { UserService } from 'src/app/modules/services/user/user.service';

@Component({
  selector: 'app-notificacao-contrato',
  templateUrl: './notificacao-contrato.component.html',
  styleUrls: ['./notificacao-contrato.component.scss'],
})
export class NotificacaoContratoComponent implements OnInit, OnDestroy {
  @Input() cnpj: any;
  @Input() buttonInclusaoDisabled: boolean = false;
  @Input() buttonExclusaoDisabled: boolean = false;
  @Input() buttonEdicaoDisabled: boolean = false;

  form!: FormGroup;
  editMode: boolean = false;
  notificaoContratoId: any = null;

  optionsTipos: any[] = [];
  searchControl = new FormControl('');
  filteredOptionsUsers: any[] = [];
  selectedOption: any = null;
  private subscriptions: Subscription[] = [];

  sortState = { sortProperty: 'criadoEm', sortDirection: 'desc' as 'asc' | 'desc' };
  columnConfig = [
    { columnDef: 'usuario', header: 'Usuário', property: 'usuario.nome' },
    { columnDef: 'email', header: 'E-mail', property: 'usuario.email' },
    { columnDef: 'tipoNotificacao', header: 'Tipo', property: 'tipoNotificacao.descricao' },
    { columnDef: 'usuario.status', header: 'Status de usuário', property: 'usuario.status', customTd: true },
    { columnDef: 'actions', header: '', custom: true },
  ];

  dataSource = new MatTableDataSource<any>();
  displayedColumns: string[] = [];
  pageIndex = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 1;

  constructor(
    private _snackBar: MatSnackBar,
    private loadingService: LoadingService,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private router: Router,
    private modalCtrl: ModalController,
    private toastService: ToastService,
    private notificacaoContratoService: NotificacaoContratoService,
    private userService: UserService
  ) {
    this.form = this.fb.group({
      idUser: ['', Validators.required],
      email: [{ value: '', disabled: true }, Validators.required],
      idTipo: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.displayedColumns = this.columnConfig.map((col) => col.columnDef);
    this.loadOptions();
    this.setupAutocomplete();
    this.getData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private loadOptions(): void {
    this.notificacaoContratoService.getTiposNotificacao().pipe(take(1)).subscribe({
      next: (res) => this.optionsTipos = res,
      error: () => this.toastService.showError('Erro ao carregar tipos.')
    });
  }

  private setupAutocomplete(): void {
    this.getFirstUsers();
    this.subscriptions.push(
      this.searchControl.valueChanges.pipe(
        tap((value) => {
          this.selectedOption = null;
          this.form.patchValue({ idUser: '' });
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            this.getFirstUsers();
          }
        }),
        debounceTime(300),
        distinctUntilChanged(),
        filter(value => typeof value === 'string' && value.trim().length > 0),
        map(value => this.normalizeString(value as string)),
        switchMap(value => this.fetchUsers(value))
      ).subscribe((results: any) => {
        this.filteredOptionsUsers = results.content || [];
      })
    );
  }

  private getFirstUsers(): void {
    this.userService.getUsersByName(0, 500, 'asc', 'nome', '', 'true').pipe(take(1)).subscribe({
      next: (results: any) => this.filteredOptionsUsers = results?.content ?? [],
      error: () => this.toastService.showError('Erro ao carregar usuários.')
    });
  }

  private normalizeString(value: string): string {
    return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }

  private fetchUsers(term: string) {
    return this.userService.getUsersByName(0, 10, 'asc', 'nome', term, 'true').pipe(
      catchError(() => of([]))
    );
  }

  onOptionSelected(event: any): void {
    const option = event.option.value;
    this.selectedOption = option;
    this.searchControl.setValue(option.nome, { emitEvent: false });
    this.form.patchValue({ idUser: option.id, email: option.email });
  }

  getData(pageIndex: number = 0): void {
    this.loadingService.showLoading();
    this.notificacaoContratoService
      .getNotificacoes(pageIndex, this.pageSize, this.sortState.sortProperty, this.sortState.sortDirection)
      .pipe(take(1), finalize(() => this.loadingService.hideLoading()))
      .subscribe({
        next: (res: any) => {
          this.dataSource.data = res.content;
          this.totalElements = res.totalElements;
          this.pageIndex = res.pageNumber;
          this.totalPages = res.totalPages;
        },
        error: (erro: any) => this.handleRequestError(erro),
      });
  }

  handleSubmit() {
    if (this.form.invalid) return;
    this.editMode ? this.update() : this.create();
  }

  create(): void {
    this.loadingService.showLoading();
    const payload = { idUsuario: this.form.value.idUser, tipoNotificacao: this.form.value.idTipo };

    this.notificacaoContratoService.create(payload).pipe(take(1), finalize(() => this.loadingService.hideLoading())).subscribe({
      next: () => {
        this.toastService.showSuccess('Notificação cadastrada com sucesso.');
        this.resetForm();
        this.getData();
      },
      error: (err) => this.handleRequestError(err)
    });
  }

  update(): void {
    this.loadingService.showLoading();
    const payload = { idUsuario: this.form.value.idUser, tipoNotificacao: this.form.value.idTipo };

    this.notificacaoContratoService.edit(this.notificaoContratoId, payload).pipe(take(1), finalize(() => this.loadingService.hideLoading())).subscribe({
      next: () => {
        this.toastService.showSuccess('Notificação atualizada com sucesso.');
        this.cancelEdit();
        this.getData();
      },
      error: (err) => this.handleRequestError(err)
    });
  }

  editar(id: any) {
    this.editMode = true;
    this.notificaoContratoId = id;
    const rowData = this.dataSource.data.find(rowData => rowData.id == id);
    if (rowData) {
      this.searchControl.setValue(rowData.usuario.nome, { emitEvent: false });
      this.selectedOption = rowData.usuario;
      this.form.patchValue({
        idUser: rowData.usuario.id,
        email: rowData.usuario.email,
        idTipo: rowData.tipoNotificacao.valor
      });
    }
  }

  cancelEdit() {
    this.editMode = false;
    this.notificaoContratoId = null;
    this.resetForm();
  }

  resetForm() {
    this.form.reset();
    this.form.get('email')?.disable();
    this.searchControl.setValue('', { emitEvent: false });
    this.selectedOption = null;
  }

  openDeleteDialog(id: number): void {
    this.dialog.open(ModalComponent, {
      data: {
        onPerformAction: (id: string) => this.confirmDelete(id),
        modalTitle: 'Excluir Notificação',
        modalContent: 'Deseja excluir este vínculo?',
        id: id,
      },
    });
  }

  private confirmDelete(id: string): void {
    this.loadingService.showLoading();
    this.notificacaoContratoService.delete(id).pipe(finalize(() => this.loadingService.hideLoading())).subscribe({
      next: () => {
        this.toastService.showSuccess('Excluído com sucesso.');
        this.getData();
      },
      error: (err) => this.handleRequestError(err)
    });
  }

  async openDialogHistorico(id: number) {
    const data = {
      type: 'notificacoes',
      id: id,
      fieldMappings: {
        usuario: 'Usuário',
        email: 'E-mail',
        tipoNotificacao: 'Tipo de Notificação',
      },
    };
    const modal = await this.modalCtrl.create({
      component: ModalHistoricComponent,
      componentProps: { data, fieldMappings: data.fieldMappings },
    });
    modal.present();
  }

  private handleRequestError(erro: any): void {
    const message = erro.error?.message || 'Erro ao processar requisição.';
    this.toastService.showError(message);
  }

  onSortChange(sortProperty: string): void {
    this.sortState.sortDirection = this.sortState.sortProperty === sortProperty && this.sortState.sortDirection === 'asc' ? 'desc' : 'asc';
    this.sortState.sortProperty = sortProperty;
    this.getData(0);
  }

  pageChangeEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.getData(event.pageIndex);
  }

  back() { this.router.navigate(['home']); }
}
